import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiPlus,
  FiLink,
  FiEdit3,
  FiFileText,
  FiCode,
  FiDatabase,
  FiFolder,
  FiTag,
  FiAlertCircle,
  FiCheck,
  FiBookOpen,
} from "react-icons/fi";
import "./LibraryAddResourceModal.css";

// ============================================
// CONSTANTS
// ============================================
const RESOURCE_TYPES = [
  {
    key: "link",
    label: "Link",
    icon: FiLink,
    description: "External URL",
    color: "#0ea5e9",
    bg: "rgba(14, 165, 233, 0.1)",
  },
  {
    key: "note",
    label: "Note",
    icon: FiEdit3,
    description: "Text content",
    color: "#10b981",
    bg: "rgba(16, 185, 129, 0.1)",
  },
  {
    key: "reference",
    label: "Reference",
    icon: FiBookOpen,
    description: "Citation/Reference",
    color: "#3b82f6",
    bg: "rgba(59, 130, 246, 0.1)",
  },
  {
    key: "code",
    label: "Code",
    icon: FiCode,
    description: "Code snippet",
    color: "#f59e0b",
    bg: "rgba(245, 158, 11, 0.1)",
  },
  {
    key: "dataset",
    label: "Dataset",
    icon: FiDatabase,
    description: "Data files",
    color: "#8b5cf6",
    bg: "rgba(139, 92, 246, 0.1)",
  },
  {
    key: "other",
    label: "Other",
    icon: FiFolder,
    description: "Other resource",
    color: "#64748b",
    bg: "rgba(100, 116, 139, 0.1)",
  },
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "#10b981" },
  { value: "normal", label: "Normal", color: "#3b82f6" },
  { value: "high", label: "High", color: "#f59e0b" },
  { value: "critical", label: "Critical", color: "#ef4444" },
];

// ============================================
// ANIMATION VARIANTS
// ============================================
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 25 },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.2 },
  },
};

// ============================================
// SUB-COMPONENTS
// ============================================

const TypeSelector = ({ selected, onSelect }) => (
  <div className="rladd-type-grid">
    {RESOURCE_TYPES.map(
      ({ key, label, icon: Icon, description, color, bg }) => (
        <motion.button
          key={key}
          type="button"
          className={`rladd-type-card ${
            selected === key ? "rladd-type-card--active" : ""
          }`}
          onClick={() => onSelect(key)}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.98 }}
          style={{
            "--type-color": color,
            "--type-bg": bg,
          }}
        >
          <div className="rladd-type-icon">
            <Icon size={20} />
          </div>
          <div className="rladd-type-info">
            <span className="rladd-type-label">{label}</span>
            <span className="rladd-type-desc">{description}</span>
          </div>
          {selected === key && (
            <motion.div
              className="rladd-type-check"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 500 }}
            >
              <FiCheck size={14} />
            </motion.div>
          )}
        </motion.button>
      )
    )}
  </div>
);

const FormField = ({ label, required, error, children }) => (
  <div className="rladd-field">
    <label className="rladd-label">
      {label}
      {required && <span className="rladd-required">*</span>}
    </label>
    {children}
    {error && (
      <motion.span
        className="rladd-error"
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <FiAlertCircle size={12} />
        {error}
      </motion.span>
    )}
  </div>
);

const PrioritySelector = ({ value, onChange }) => (
  <div className="rladd-priority-group">
    {PRIORITIES.map(({ value: val, label, color }) => (
      <motion.button
        key={val}
        type="button"
        className={`rladd-priority-btn ${
          value === val ? "rladd-priority-btn--active" : ""
        }`}
        onClick={() => onChange(val)}
        style={{
          "--priority-color": color,
        }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <span className="rladd-priority-dot" style={{ background: color }} />
        {label}
      </motion.button>
    ))}
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const LibraryAddResourceModal = ({ roomId, onClose, onCreateResource }) => {
  const [resourceType, setResourceType] = useState("link");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    url: "",
    noteContent: "",
    folder: "General",
    tags: "",
    priority: "normal",
    doi: "",
    authors: "",
    year: "",
    venue: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (resourceType === "link" && !formData.url.trim()) {
      newErrors.url = "URL is required for links";
    }

    if (resourceType === "note" && !formData.noteContent.trim()) {
      newErrors.noteContent = "Content is required for notes";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const resourceData = {
        resourceType,
        title: formData.title.trim(),
        description: formData.description.trim(),
        folder: formData.folder.trim() || "General",
        tags: formData.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        priority: formData.priority,
      };

      if (resourceType === "link") {
        resourceData.url = formData.url.trim();
      } else if (resourceType === "note") {
        resourceData.noteContent = formData.noteContent.trim();
      } else if (resourceType === "reference") {
        // Reference type uses citation object for DOI, authors, year, venue
        resourceData.citation = {
          doi: formData.doi.trim() || undefined,
          authors: formData.authors
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean),
          year: parseInt(formData.year) || new Date().getFullYear(),
          venue: formData.venue.trim() || undefined,
          style: "APA",
          formatted: `${formData.authors} (${formData.year || new Date().getFullYear()}). ${formData.title}. ${formData.venue || ""}`.trim(),
        };
      } else if (resourceType === "code") {
        // Code type - add code content fields
        resourceData.codeContent = {
          code: formData.codeContent || "",
          language: formData.codeLanguage || "javascript",
          repository: formData.codeRepository || undefined,
        };
      } else if (resourceType === "dataset") {
        // Dataset can have an optional URL
        if (formData.url.trim()) {
          resourceData.url = formData.url.trim();
        }
      }
      // "other" type doesn't need additional fields

      await onCreateResource(resourceData);
      onClose();
    } catch (error) {
setErrors({ submit: error.message || "Failed to create resource" });
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  return (
    <motion.div
      className="rladd-overlay"
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      <motion.div
        className="rladd-modal"
        variants={modalVariants}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="rladd-header">
          <div className="rladd-header-content">
            <div className="rladd-header-icon">
              <FiPlus size={20} />
            </div>
            <div>
              <h2 className="rladd-title">Add Resource</h2>
              <p className="rladd-subtitle">
                Add a new resource to your library
              </p>
            </div>
          </div>
          <motion.button
            className="rladd-close"
            onClick={onClose}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiX size={20} />
          </motion.button>
        </header>

        {/* Form */}
        <form className="rladd-body" onSubmit={handleSubmit}>
          {/* Resource Type */}
          <div className="rladd-section">
            <h3 className="rladd-section-title">Resource Type</h3>
            <TypeSelector selected={resourceType} onSelect={setResourceType} />
          </div>

          {/* Basic Info */}
          <div className="rladd-section">
            <h3 className="rladd-section-title">Basic Information</h3>

            <FormField label="Title" required error={errors.title}>
              <input
                type="text"
                className="rladd-input"
                value={formData.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder="Enter resource title"
                disabled={submitting}
              />
            </FormField>

            <FormField label="Description">
              <textarea
                className="rladd-textarea"
                value={formData.description}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="Add a description..."
                rows={3}
                disabled={submitting}
              />
            </FormField>
          </div>

          {/* Type-Specific Fields */}
          <AnimatePresence mode="wait">
            {resourceType === "link" && (
              <motion.div
                key="link-fields"
                className="rladd-section"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="rladd-section-title">Link Details</h3>
                <FormField label="URL" required error={errors.url}>
                  <div className="rladd-input-with-icon">
                    <FiLink className="rladd-input-icon" size={16} />
                    <input
                      type="url"
                      className="rladd-input rladd-input--with-icon"
                      value={formData.url}
                      onChange={(e) => updateField("url", e.target.value)}
                      placeholder="https://example.com"
                      disabled={submitting}
                    />
                  </div>
                </FormField>
              </motion.div>
            )}

            {resourceType === "note" && (
              <motion.div
                key="note-fields"
                className="rladd-section"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="rladd-section-title">Note Content</h3>
                <FormField label="Content" required error={errors.noteContent}>
                  <textarea
                    className="rladd-textarea rladd-textarea--large"
                    value={formData.noteContent}
                    onChange={(e) => updateField("noteContent", e.target.value)}
                    placeholder="Write your note here..."
                    rows={6}
                    disabled={submitting}
                  />
                </FormField>
              </motion.div>
            )}

            {resourceType === "reference" && (
              <motion.div
                key="reference-fields"
                className="rladd-section"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="rladd-section-title">Reference Details</h3>
                <FormField label="DOI">
                  <input
                    type="text"
                    className="rladd-input"
                    value={formData.doi}
                    onChange={(e) => updateField("doi", e.target.value)}
                    placeholder="10.1234/example"
                    disabled={submitting}
                  />
                </FormField>
                <FormField label="Authors (comma-separated)">
                  <input
                    type="text"
                    className="rladd-input"
                    value={formData.authors}
                    onChange={(e) => updateField("authors", e.target.value)}
                    placeholder="John Doe, Jane Smith"
                    disabled={submitting}
                  />
                </FormField>
                <div className="rladd-row">
                  <FormField label="Year">
                    <input
                      type="number"
                      className="rladd-input"
                      value={formData.year}
                      onChange={(e) => updateField("year", e.target.value)}
                      placeholder="2024"
                      min="1900"
                      max={new Date().getFullYear() + 1}
                      disabled={submitting}
                    />
                  </FormField>
                  <FormField label="Venue/Journal">
                    <input
                      type="text"
                      className="rladd-input"
                      value={formData.venue}
                      onChange={(e) => updateField("venue", e.target.value)}
                      placeholder="Nature, IEEE, etc."
                      disabled={submitting}
                    />
                  </FormField>
                </div>
              </motion.div>
            )}

            {resourceType === "code" && (
              <motion.div
                key="code-fields"
                className="rladd-section"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="rladd-section-title">Code Details</h3>
                <FormField label="Code" required error={errors.codeContent}>
                  <textarea
                    className="rladd-textarea rladd-textarea--large"
                    value={formData.codeContent || ""}
                    onChange={(e) => updateField("codeContent", e.target.value)}
                    placeholder="Paste your code here..."
                    rows={8}
                    disabled={submitting}
                    style={{ fontFamily: "monospace" }}
                  />
                </FormField>
                <div className="rladd-row">
                  <FormField label="Language">
                    <select
                      className="rladd-input"
                      value={formData.codeLanguage || "javascript"}
                      onChange={(e) => updateField("codeLanguage", e.target.value)}
                      disabled={submitting}
                    >
                      <option value="javascript">JavaScript</option>
                      <option value="python">Python</option>
                      <option value="java">Java</option>
                      <option value="cpp">C++</option>
                      <option value="csharp">C#</option>
                      <option value="go">Go</option>
                      <option value="rust">Rust</option>
                      <option value="r">R</option>
                      <option value="matlab">MATLAB</option>
                      <option value="sql">SQL</option>
                      <option value="bash">Bash</option>
                      <option value="other">Other</option>
                    </select>
                  </FormField>
                  <FormField label="Repository URL">
                    <input
                      type="url"
                      className="rladd-input"
                      value={formData.codeRepository || ""}
                      onChange={(e) => updateField("codeRepository", e.target.value)}
                      placeholder="https://github.com/..."
                      disabled={submitting}
                    />
                  </FormField>
                </div>
              </motion.div>
            )}

            {resourceType === "dataset" && (
              <motion.div
                key="dataset-fields"
                className="rladd-section"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="rladd-section-title">Dataset Details</h3>
                <FormField label="Data Source URL">
                  <div className="rladd-input-with-icon">
                    <FiLink className="rladd-input-icon" size={16} />
                    <input
                      type="url"
                      className="rladd-input rladd-input--with-icon"
                      value={formData.url}
                      onChange={(e) => updateField("url", e.target.value)}
                      placeholder="https://drive.google.com/... or data repository link"
                      disabled={submitting}
                    />
                  </div>
                </FormField>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Organization */}
          <div className="rladd-section">
            <h3 className="rladd-section-title">Organization</h3>

            <div className="rladd-row">
              <FormField label="Folder">
                <div className="rladd-input-with-icon">
                  <FiFolder className="rladd-input-icon" size={16} />
                  <input
                    type="text"
                    className="rladd-input rladd-input--with-icon"
                    value={formData.folder}
                    onChange={(e) => updateField("folder", e.target.value)}
                    placeholder="General"
                    disabled={submitting}
                  />
                </div>
              </FormField>
              <FormField label="Priority">
                <PrioritySelector
                  value={formData.priority}
                  onChange={(val) => updateField("priority", val)}
                />
              </FormField>
            </div>

            <FormField label="Tags (comma-separated)">
              <div className="rladd-input-with-icon">
                <FiTag className="rladd-input-icon" size={16} />
                <input
                  type="text"
                  className="rladd-input rladd-input--with-icon"
                  value={formData.tags}
                  onChange={(e) => updateField("tags", e.target.value)}
                  placeholder="research, important, review"
                  disabled={submitting}
                />
              </div>
            </FormField>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <motion.div
              className="rladd-submit-error"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <FiAlertCircle size={16} />
              {errors.submit}
            </motion.div>
          )}
        </form>

        {/* Footer */}
        <footer className="rladd-footer">
          <motion.button
            type="button"
            className="rladd-btn rladd-btn--secondary"
            onClick={onClose}
            disabled={submitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Cancel
          </motion.button>
          <motion.button
            type="submit"
            className="rladd-btn rladd-btn--primary"
            onClick={handleSubmit}
            disabled={submitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {submitting ? (
              <>
                <motion.span
                  className="rladd-spinner"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
                Creating...
              </>
            ) : (
              <>
                <FiPlus size={16} />
                Create Resource
              </>
            )}
          </motion.button>
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default LibraryAddResourceModal;
